import { ModalProvider, useModal } from '@/components/ModalContext';
import { getJSON } from '@/functions/get-json';
import { globalSignalR } from '@/services/globalSignalR';
import { useGlobalState } from '@/shared/store/global';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCircleCheck,
  faCircleExclamation,
  faEnvelope,
  faGrid,
  faInfo,
  faQuestionCircle,
  faRepeat,
  faSensorTriangleExclamation,
  faTimes,
} from '@fortawesome/sharp-solid-svg-icons';
import { webAppApi, type BannerDto } from '@lib/api';
import { SCBanner } from '@lib/sc-eone-ui/src/banner';
import { Labels } from '@lib/sc-eone-ui/src/strings/strings-en-us';
import { Button } from 'eone-ui/buttons/button';
import { HorizontalDragPanel } from 'eone-ui/drag-handle/drag-panel';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { PagesRoutes } from 'virtual:react-pages';
import './css/layout.css';
import { Email } from './help/email-sidebar';
import { Help } from './help/help-sidebar';
import Logo_Header_eOneWhite from './images/eone-white.svg';
import SmartConnectLogo from './images/smartconnect-logo.svg';
import { Menu } from './menu/menu';
import { Notifications } from './notifications/notifications-sidebar';
import { ProfileButton } from './profile-button/profile-button';
import { SwitchAccountModal } from './switch-account';
import { loadTheme } from './widget-themes';

function MenuToggle(props) {
  const getIcon = () => {
    return props.Open ? (props.Animating ? faGrid : faTimes) : props.Animating ? faTimes : faGrid;
  };

  if (props.Visible !== undefined && !props.Visible) {
    return null;
  }
  const title = props.Open ? Labels.CloseMenu : Labels.ShowMenu;
  const dataTestId = 'test'; //getDataTestId(props.DataTestId, title);
  const iconClass = `pd-menu-icon ${props.Animating ? 'pd-menu-icon-flipping' : ''} ${props.Open ? 'open' : 'closed'}`;
  return (
    <a
      onClick={props.OnClick}
      accessKey="m"
      className="pd-app-header-menu-button"
      title={title}
      data-testid={dataTestId}
    >
      <FontAwesomeIcon className={iconClass} icon={getIcon()} fixedWidth size={'lg'} />
    </a>
  );
}

const useSidebarOpen = () => {
  const [state, setState] = useState(AnimationState.Closed);
  const [type, setType] = useState();

  const updateStateWithDelay = (newState, delay, callback) => {
    setState(newState);
    if (delay) {
      setTimeout(() => callback(), delay);
    }
  };

  const open = (openType) => {
    if (state === AnimationState.Closed || state === AnimationState.Closing) {
      updateStateWithDelay(AnimationState.Opening, 150, () => setState(AnimationState.Open));
    }
    setType(openType);
  };

  const close = () => {
    updateStateWithDelay(AnimationState.Closing, 150, () => setState(AnimationState.Closed));
    setType(undefined);
  };

  const closeInstantly = () => {
    setType(undefined);
    setState(AnimationState.Closed);
  };

  const toggle = (openType) => {
    if ((state === AnimationState.Open || state === AnimationState.Opening) && openType === type) {
      close();
    } else {
      open(openType);
    }
  };

  return {
    state,
    type,
    open,
    close,
    toggle,
    closeInstantly,
    isVisible: state !== AnimationState.Closed,
    isOpen: state === AnimationState.Open || state === AnimationState.Opening,
    isAnimating: state === AnimationState.Opening || state === AnimationState.Closing,
  };
};

const useMenuOpen = () => {
  const [state, setState] = useState(AnimationState.Closed);

  const updateStateWithDelay = (newState, delay, callback) => {
    setState(newState);
    if (delay) {
      setTimeout(() => callback(), delay);
    }
  };

  const open = () => {
    updateStateWithDelay(AnimationState.Opening, 150, () => setState(AnimationState.Open));
  };

  const close = () => {
    updateStateWithDelay(AnimationState.Closing, 150, () => setState(AnimationState.Closed));
  };

  const closeInstantly = () => {
    setState(AnimationState.Closed);
  };

  const toggle = () => {
    if (state === AnimationState.Open || state === AnimationState.Opening) {
      close();
    } else {
      open();
    }
  };

  return {
    state,
    open,
    close,
    toggle,
    closeInstantly,
    isVisible: state !== AnimationState.Closed,
    isOpen: state === AnimationState.Open || state === AnimationState.Opening,
    isAnimating: state === AnimationState.Opening || state === AnimationState.Closing,
  };
};

function EOneLogo(props) {
  if (props.Disabled) {
    return (
      <Link className="pd-app-header-eone-logo" data-testid={props.DataTestId}>
        <img
          src={Logo_Header_eOneWhite}
          alt="eOne logo"
          className="pd-eone-logo"
          width="160"
          height="44"
        />
      </Link>
    );
  }
  return (
    <Link className="pd-app-header-eone-logo" data-testid={props.DataTestId}>
      <img
        src={Logo_Header_eOneWhite}
        alt="eOne logo"
        className="pd-eone-logo"
        width="160"
        height="44"
      />
    </Link>
  );
}

function SmartconnectLogo() {
  return (
    <Link to={PagesRoutes.HOME}>
      <img
        src={SmartConnectLogo}
        alt=""
        className="pd-popdock-logo"
        style={{ height: 16, width: 162 }}
      />
    </Link>
  );
}

const AnimationState = {
  Open: 0,
  Opening: 1,
  Closed: 2,
  Closing: 3,
};

export interface HelpArticle {
  Name: string;
  Description: string;
  Slug: string;
  File: string;
  Icon: string;
  Children: HelpArticle[];
  PinToTop?: boolean;
}

export function Layout() {
  return (
    // SC-1360: HelpProvider used to be here. It moved up to WrapApp (src/app/index.tsx) so that it is also an
    // ancestor of SCComponentRoot, where SCModal renders. It must NOT be re-added here: a second provider would
    // create a second, separate article stack, so the help sidebar and a modal's help link would push to
    // different stacks and one of them would silently do nothing. The sidebar still works because it is a
    // descendant of the hoisted provider.
    <ModalProvider>
      <Index>
        <Outlet />
      </Index>
    </ModalProvider>
  );
}

function Index(props) {
  const { show } = useModal();
  const { userSession } = useGlobalState();
  const settingsBaseUrl = 'https://reactblobstorageeqa.blob.core.windows.net';
  const location = useLocation();
  const {
    open: openSidebar,
    close: closeSidebar,
    toggle: toggleSidebar,
    isOpen: sidebarOpen,
    isVisible: sidebarVisible,
    type: sidebarType,
  } = useSidebarOpen();
  const {
    close: closeMenu,
    closeInstantly: closeMenuInstantly,
    toggle: toggleMenu,
    isOpen: menuOpen,
    isAnimating: menuAnimating,
    isVisible: menuVisible,
  } = useMenuOpen();
  const [help, setHelp] = useState<HelpArticle[]>([]);
  const [helpLoaded, setHelpLoaded] = useState(false);
  const [calculatedFields, setCalculatedFields] = useState([]);
  const [calculatedFieldsLoaded, setCalculatedFieldsLoaded] = useState(false);
  const [systemBanner, setSystemBanner] = useState<BannerDto[] | null>(null);
  // SC-1506: temporary. The in-app help panel has no content yet, so the header help button opens
  // this configured URL (sc-help-url) in a new tab instead. Remove once real help ships.
  const [helpUrl, setHelpUrl] = useState('');

  // Notifications state
  const [unreadCount, setUnreadCount] = useState(0);

  const openHelpSidebar = () => {
    openSidebar('help');
  };

  useEffect(() => {
    if (userSession) {
      globalSignalR
        .initialize()
        .then(() => globalSignalR.loadHistory())
        .catch((error) => {
          console.error('SignalR initialization failed:', error);
        });

      const unsubscribe = globalSignalR.onMessagesChange((messages) => {
        const unreadCount = messages.filter((m) => !m.read).length;
        setUnreadCount(unreadCount);
      });

      return unsubscribe;
    }
  }, [userSession]);

  useEffect(() => {
    if (!userSession) return;
    let isMounted = true;
    const loadBanner = async () => {
      try {
        const response = await webAppApi.systemEvent.getBanner();
        const data = response.data?.data;
        if (!isMounted) return;
        if (data) {
          setSystemBanner(data);
        } else {
          setSystemBanner(null);
        }
      } catch (error) {
        console.error('Failed to load system banner', error);
      }
    };

    loadBanner();
    const intervalId = window.setInterval(loadBanner, 5 * 60 * 1000);
    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [userSession]);

  // SC-1506: see the helpUrl state above.
  useEffect(() => {
    void fetch('/api/settings/help-url')
      .then((response) => response.json())
      .then((json) => setHelpUrl(json?.data?.helpUrl ?? ''))
      .catch((error) => console.error('Failed to load help url', error));
  }, []);

  const loadHelp = () => {
    if (helpLoaded) return;
    getJSON(settingsBaseUrl, 'documentation', 'help.json', loadHelpFunc);
    setHelpLoaded(true);
  };

  const loadCalculatedFields = () => {
    if (calculatedFieldsLoaded) return;
    getJSON(
      settingsBaseUrl,
      'documentation',
      'popdockscript-functions.json',
      loadCalculatedFieldsFunc,
    );
    setCalculatedFieldsLoaded(true);
  };

  const loadHelpFunc = (helpJson) => {
    setHelp(helpJson);
  };

  const loadCalculatedFieldsFunc = (functionsJson) => {
    const newCalculatedFields = JSON.parse(JSON.stringify(functionsJson));
    setCalculatedFields(newCalculatedFields);
  };

  useEffect(() => {
    closeMenuInstantly();
    closeSidebar();
  }, [location]);

  const showSwitchAccountModal = useCallback(() => {
    show(({ close }) => {
      return <SwitchAccountModal OnCancel={close} />;
    });
  }, [show]);

  const switchAccountMenuItems = useMemo(() => {
    if (userSession?.multipleAccounts) {
      return [
        {
          Label: Labels.SwitchAccount,
          Icon: faRepeat,
          OnClick: showSwitchAccountModal,
        },
      ];
    }
  }, [userSession, showSwitchAccountModal]);

  const [currentSystemBannerMessage, setCurrentSystemBannerMessage] = useState<BannerDto | null>(
    null,
  );
  const currentBannerIndex = useRef(0);
  const BANNER_INTERVAL = 8 * 1000;

  useEffect(() => {
    if (systemBanner == null || systemBanner.length == 0) return;
    const interval = setInterval(() => {
      setCurrentSystemBannerMessage(systemBanner[currentBannerIndex.current]);
      currentBannerIndex.current =
        currentBannerIndex.current + 1 >= systemBanner.length ? 0 : currentBannerIndex.current + 1;
    }, BANNER_INTERVAL);

    return () => {
      if (systemBanner) {
        clearInterval(interval);
      }
    };
  }, [systemBanner, BANNER_INTERVAL]);

  useEffect(() => {
    if (userSession) {
      const theme = userSession.theme ?? '';
      loadTheme(theme);
    }
  }, [userSession]);

  const currentMessageInfo = useMemo(() => {
    if (!currentSystemBannerMessage) return { icon: null, color: '' };

    let colour;
    let icon;
    switch (currentSystemBannerMessage.type) {
      case 'info': {
        colour = 'blue';
        icon = faInfo;
        break;
      }
      case 'success': {
        colour = 'green';
        icon = faCircleCheck;
        break;
      }
      case 'danger': {
        colour = 'red';
        icon = faCircleExclamation;
        break;
      }
      case 'warning': {
        colour = 'orange';
        icon = faSensorTriangleExclamation;
        break;
      }
      default: {
        colour = 'blue';
        break;
      }
    }

    return { icon, colour };
  }, [currentSystemBannerMessage]);

  return (
    <div style={{ display: 'flex' }}>
      <div className="pd-app-body">
        {currentSystemBannerMessage && (
          <div className="pd-top-banner">
            <SCBanner
              Text={currentSystemBannerMessage.message ?? ''}
              Colour={currentMessageInfo.colour}
              Icon={currentMessageInfo.icon}
            />
          </div>
        )}
        <div className="pd-app-header">
          <div className="pd-app-header-left">
            <MenuToggle
              Visible={true}
              Open={menuOpen}
              Animating={menuAnimating}
              OnClick={toggleMenu}
              Disabled={props.IsPageDisabled}
              DataTestId="page-header.menu"
            />
            <SmartconnectLogo />
            <div className="pd-app-header-version-container">
              <label className="pd-app-header-version-label">Version: {window.__version}</label>
            </div>
          </div>
          <div className="pd-app-header-right">
            <ProfileButton
              Disabled={props.IsPageDisabled}
              UserName={userSession?.email}
              Email={userSession?.email}
              AvatarUrl="" //{userSession.AvatarUrl}
              IsEmbeddedApp={false}
              DataTestId="page-header.profile"
            />
            <div className="pd-app-header-account">
              <Button
                Label={userSession?.accountName}
                Title={
                  userSession?.multipleAccounts ? Labels.SwitchAccount : userSession?.accountName
                }
                Disabled={!userSession?.multipleAccounts}
                MenuItems={switchAccountMenuItems}
                DataTestId="page-header.switch-account-button"
              />
            </div>
            <Button
              OnClick={() => toggleSidebar('email')}
              Title={Labels.Email}
              Icon={faEnvelope}
              DataTestId="page-header.email-button"
            />
            <Button
              OnClick={() => window.open(helpUrl, '_blank', 'noopener,noreferrer')}
              Title={Labels.Help}
              Icon={faQuestionCircle}
              DataTestId="page-header.help-button"
            />
            <div style={{ position: 'relative' }}>
              <Button
                OnClick={() => toggleSidebar('notifications')}
                Title={Labels.Notifications}
                Icon={faBell}
                DataTestId="page-header.notifications-button"
              />
              {unreadCount > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-2px',
                    right: '-2px',
                    backgroundColor: '#ff4d4f',
                    color: 'white',
                    borderRadius: '10px',
                    minWidth: '18px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    border: '2px solid white',
                    pointerEvents: 'none',
                  }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </div>
              )}
            </div>
            <EOneLogo Disabled={props.IsPageDisabled} />
          </div>
        </div>
        <div className="pd-app-main">
          <div className={`pd-menu-container ${menuOpen ? 'open' : 'closed'}`}>
            <Menu
              Visible={menuVisible}
              Connectors={props.Connectors}
              ConnectorSettings={props.ConnectorSettings}
              UserFeatures={props.UserFeatures}
              AccountFeatures={props.AccountFeatures}
              UserEmail={userSession?.email}
              Open={menuOpen}
              OnClose={closeMenu}
              SetErrorModal={props.SetErrorModal}
              SetConfirmationModal={props.SetConfirmationModal}
            />
          </div>
          <div className="pd-setup-page">{props.children}</div>
        </div>
        <div id="modal-root" className="modal-root" />
      </div>
      <HorizontalDragPanel
        PanelVisible={sidebarOpen}
        MinWidth={200}
        MaxWidth={800}
        InitialWidth={420}
      >
        <div className="pd-help-panel">
          <Help
            IsOpen={sidebarVisible && sidebarType === 'help'}
            SettingsBaseUrl={settingsBaseUrl}
            OpenHelpSidebar={openHelpSidebar}
            OnClose={closeSidebar}
            Help={help}
            CalculatedFields={calculatedFields}
            LoadHelp={loadHelp}
            LoadCalculatedFields={loadCalculatedFields}
          />
          <Email Open={sidebarVisible && sidebarType === 'email'} OnClose={closeSidebar} />
          <Notifications
            Open={sidebarVisible && sidebarType === 'notifications'}
            OnClose={closeSidebar}
          />
        </div>
      </HorizontalDragPanel>
    </div>
  );
}
